import { ChordLyricsPair, ChordProParser } from "chordsheetjs";
import { Key } from "@tonaljs/tonal";

// List of all musical notes in order (using sharps)
const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

// List of all musical notes using flats
const NOTES_FLAT = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

// Map for flat to sharp conversion
const FLAT_TO_SHARP: Record<string, string> = {
  Db: "C#",
  Eb: "D#",
  Fb: "E",
  Gb: "F#",
  Ab: "G#",
  Bb: "A#",
  Cb: "B",
};

// Map for sharp to flat conversion
const SHARP_TO_FLAT: Record<string, string> = {
  "C#": "Db",
  "D#": "Eb",
  "E#": "F",
  "F#": "Gb",
  "G#": "Ab",
  "A#": "Bb",
  "B#": "C",
};

/**
 * Get the semitone index of a note (0-11)
 */
function getNoteIndex(note: string): number {
  const root = note.endsWith("m") ? note.slice(0, -1) : note;
  const normalized = FLAT_TO_SHARP[root] || root;
  return NOTES.indexOf(normalized);
}

/**
 * Calculate semitones between two keys
 */
export function getSemitonesBetween(fromKey: string, toKey: string): number {
  const fromIndex = getNoteIndex(fromKey);
  const toIndex = getNoteIndex(toKey);
  if (fromIndex === -1 || toIndex === -1) return 0;
  return (toIndex - fromIndex + 12) % 12;
}

/**
 * Get all available keys for transposition
 */
export function getAllKeys(useFlats: boolean = false): string[] {
  return useFlats ? [...NOTES_FLAT] : [...NOTES];
}

/**
 * Convert a key between sharp and flat notation
 */
export function convertKeyNotation(key: string, toFlat: boolean): string {
  const isMinor = key.endsWith("m");
  const root = isMinor ? key.slice(0, -1) : key;

  let newRoot = root;
  if (toFlat && SHARP_TO_FLAT[root]) {
    newRoot = SHARP_TO_FLAT[root];
  } else if (!toFlat && FLAT_TO_SHARP[root]) {
    newRoot = FLAT_TO_SHARP[root];
  }

  return isMinor ? `${newRoot}m` : newRoot;
}

/**
 * Whether a key conventionally prefers flat over sharp spelling, per its
 * key signature (e.g. F major / D minor -> flats; D major -> sharps) —
 * backed by @tonaljs/tonal's circle-of-fifths key data. A global sharp/flat
 * toggle applied regardless of key produced wrong notation (e.g. showing
 * "A#" for an F-major song's Bb chord); this gives the musically correct
 * default for whatever key a song is currently displayed in.
 */
export function getPreferredAccidental(key: string): boolean {
  const isMinor = key.endsWith("m");
  const tonic = isMinor ? key.slice(0, -1) : key;
  const info = isMinor ? Key.minorKey(tonic) : Key.majorKey(tonic);
  return info.alteration < 0;
}

/**
 * Extract all unique chords from ChordPro content, transposed from
 * `fromKey` to `toKey` and normalized to the requested accidental.
 */
export function extractChordsFromContent(
  content: string,
  fromKey: string,
  toKey: string,
  useFlats: boolean = false,
): string[] {
  const semitones = getSemitonesBetween(fromKey, toKey);
  // Only re-spell when transposing or explicitly asked for flats — an
  // unconditional useAccidental() would force every flat-root chord (e.g.
  // "Bb") into its sharp spelling ("A#") even at the original, untransposed
  // key, which is wrong notation for keys that are conventionally flat.
  let song = new ChordProParser().parse(content);
  if (semitones !== 0) song = song.transpose(semitones);
  if (semitones !== 0 || useFlats) {
    song = song.useAccidental(useFlats ? "b" : "#");
  }
  return song.getChords();
}

/**
 * Whether content is written in valid ChordPro syntax — parses without
 * throwing AND contains at least one bracketed chord (content that parses
 * cleanly but has zero chords is plain text or a non-ChordPro format like
 * chords-over-lyrics, not actually ChordPro).
 */
export function isValidChordProContent(content: string): boolean {
  try {
    const song = new ChordProParser().parse(content);
    return song.getChords().length > 0;
  } catch {
    return false;
  }
}

/**
 * Extract pure lyrics from ChordPro content by removing chords and
 * directives. Lyrics don't change with key, so this does not transpose.
 */
export function extractLyricsFromChordPro(content: string): string {
  if (!content) return "";

  const song = new ChordProParser().parse(content);
  const lines: string[] = [];

  song.lines.forEach((line) => {
    const text = line.items
      .filter((item): item is ChordLyricsPair => item instanceof ChordLyricsPair)
      .map((item) => item.lyrics ?? "")
      .join("")
      .trim();
    if (text.length > 0) lines.push(text);
  });

  return lines.join("\n");
}
